import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getSpecialReportsSelector } from '../selectors'
import { setCategories, setSubCategories, saveSpecialReport } from '../store/specialReports.actions'
import {
  getSpecialReportCategoriesSelector,
  getSpecialReportSubCategoriesSelector,
} from '../../options/selectors/specialReportOptions.selector'
import { IOption } from '../../../common/common.types'
import { findMatchingObject } from '../../../utilities/array.utils'
import { ICategory } from '../store/specialReports.types'
import CategoryItem from './CategoryItem'

const ReportType: React.FC = () => {
  const dispatch = useDispatch()
  const specialReportsData = useSelector(getSpecialReportsSelector)
  const { categories, subCategories, ruleId } = specialReportsData
  const dynamicCategories = useSelector(getSpecialReportCategoriesSelector)

  const dynamicSubCategories = useSelector(getSpecialReportSubCategoriesSelector)
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(categories[0]?.elementId)
  const isCategoryDisabled = !!(ruleId && categories.length)

  const getSubCategoriesForCategory = (categoryId: string): ICategory[] => {
    if (!categories.length) return []

    return dynamicSubCategories.filter((subCategory) => {
      const parentId = subCategory.properties.find((p) => p.key === 'categoryId')?.value
      return parentId === categoryId
    })
  }

  const isCategorySelected = (categoryId: string): boolean => {
    return findMatchingObject(categories, { id: Number(categoryId) }) !== -1
  }

  const handleCategoryToggle = (category: ICategory) => {
    const categoryId = category.elementId
    const isCurrentlySelected = isCategorySelected(categoryId)

    if (isCurrentlySelected) {
      dispatch(setCategories([]))
    } else {
      const categoryOption: IOption = { ...category, id: Number(categoryId), name: category.label }
      dispatch(setCategories([categoryOption]))
    }
    dispatch(setSubCategories([]))
    setExpandedCategoryId(null)
    dispatch(saveSpecialReport())
  }

  const handleSubCategoryToggle = (subCategory: ICategory, checked: boolean) => {
    const subCategoryOption: IOption = {
      id: Number(subCategory.elementId),
      name: subCategory.label,
    }

    let updatedSubCategories: IOption[]

    if (checked) {
      const isAlreadySelected = subCategories.some((sub) => sub.id === subCategoryOption.id)
      updatedSubCategories = isAlreadySelected ? subCategories : [...subCategories, subCategoryOption]
    } else {
      updatedSubCategories = subCategories.filter((sub: IOption) => sub.id !== subCategoryOption.id)
    }

    dispatch(setSubCategories(updatedSubCategories))
    dispatch(saveSpecialReport())
  }

  const handleExpandToggle = (categoryId: string) => {
    setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId))
  }

  return (
    <>
      {dynamicCategories.map((category) => {
        const categoryId = category.elementId
        const isSelected = isCategorySelected(categoryId)
        const categorySubCategories = isSelected ? getSubCategoriesForCategory(categoryId) : []
        const isExpanded = expandedCategoryId === categoryId

        return (
          <CategoryItem
            key={categoryId}
            category={category}
            subCategories={categorySubCategories}
            isSelected={isSelected}
            isExpanded={isExpanded}
            selectedSubCategories={subCategories}
            isCategoryDisabled={isCategoryDisabled}
            onCategoryToggle={handleCategoryToggle}
            onExpandToggle={handleExpandToggle}
            onSubCategoryToggle={handleSubCategoryToggle}
          />
        )
      })}
    </>
  )
}

export default ReportType
